import { useCallback, useEffect, useRef, useState, type ChangeEvent } from 'react';
import { isSafeLink } from '@sarnia/shared';
import { Icon } from '../../../components/ui/Icon';
import type { PanelCtx } from '../panelTypes';

interface LinkRowProps {
  index: number;
  label: string;
  defaultHref: string;
  override: string | undefined;
  ctx: PanelCtx;
  registerRef: (index: number, el: HTMLDivElement | null) => void;
}

function LinkRow({ index, label, defaultHref, override, ctx, registerRef }: LinkRowProps) {
  const [value, setValue] = useState(override ?? '');
  const [error, setError] = useState<string | null>(null);
  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    setValue(override ?? '');
    setError(null);
  }, [override]);

  useEffect(() => {
    return () => {
      if (timerRef.current !== null) window.clearTimeout(timerRef.current);
    };
  }, []);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value.trim();
    setValue(newValue);
    setError(null);

    if (timerRef.current !== null) window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => {
      if (newValue === '') {
        const nextLinks: Record<string, string> = {};
        for (const [key, value] of Object.entries(ctx.props.links ?? {})) {
          if (key !== String(index)) nextLinks[key] = value;
        }
        ctx.update({ links: nextLinks });
        ctx.post({ type: 'setLink', id: String(index), href: defaultHref });
        return;
      }
      if (!isSafeLink(newValue)) {
        setError('Use https://, http://, mailto:, tel:, # or /');
        return;
      }
      ctx.update({ links: { ...(ctx.props.links ?? {}), [String(index)]: newValue } });
      ctx.post({ type: 'setLink', id: String(index), href: newValue });
    }, 300);
  };

  const handleReset = () => {
    if (timerRef.current !== null) window.clearTimeout(timerRef.current);
    setValue('');
    setError(null);
    const nextLinks: Record<string, string> = {};
    for (const [key, value] of Object.entries(ctx.props.links ?? {})) {
      if (key !== String(index)) nextLinks[key] = value;
    }
    ctx.update({ links: nextLinks });
    ctx.post({ type: 'setLink', id: String(index), href: defaultHref });
  };

  const isOverridden = override !== undefined && override !== defaultHref;

  return (
    <div
      className={`dw-link-row ${error ? 'dw-link-row--error' : ''} ${isOverridden ? 'dw-link-row--overridden' : ''}`}
      ref={(el) => registerRef(index, el)}
    >
      <div className="dw-link-info">
        <span className="dw-link-label">{label}</span>
        {isOverridden ? (
          <span className="dw-link-override-badge">Overridden</span>
        ) : null}
      </div>
      <div className="dw-link-controls">
        <input
          type="text"
          className="dw-input"
          placeholder={defaultHref}
          value={value}
          onChange={handleChange}
          aria-label={`URL for ${label}`}
        />
        {error ? (
          <div className="dw-link-error" role="alert">
            {error}
          </div>
        ) : null}
        <div className="dw-link-actions">
          <button
            type="button"
            className="dw-button dw-button--ghost"
            onClick={() => ctx.post({ type: 'focus', selector: `[data-lnk="${index}"]` })}
          >
            <Icon name="eye" size={16} />
            <span>Locate</span>
          </button>
          {isOverridden ? (
            <button
              type="button"
              className="dw-button dw-button--ghost"
              onClick={handleReset}
            >
              <Icon name="undo" size={16} />
              <span>Reset</span>
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default function LinksPanel({ ctx }: { ctx: PanelCtx }) {
  const [search, setSearch] = useState('');
  const rowRefs = useRef<Array<HTMLDivElement | null>>([]);

  const links = ctx.entry.links;
  const filteredLinks = search.trim()
    ? links
        .map((link, index) => ({ ...link, index }))
        .filter((link) => link.label.toLowerCase().includes(search.toLowerCase()))
    : links.map((link, index) => ({ ...link, index }));

  const registerRef = useCallback((index: number, el: HTMLDivElement | null) => {
    rowRefs.current[index] = el;
  }, []);

  useEffect(() => {
    if (!ctx.selection || ctx.selection.kind !== 'link') return;
    const index = Number(ctx.selection.id);
    if (Number.isInteger(index) && index >= 0 && index < links.length) {
      const el = rowRefs.current[index];
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.classList.add('dw-pulse');
        window.setTimeout(() => el.classList.remove('dw-pulse'), 1400);
      }
    }
  }, [ctx.selection, links.length]);

  if (links.length === 0) {
    return (
      <div className="dw-panel">
        <div className="dw-panel-heading">
          <Icon name="link" size={18} />
          <h2>Links</h2>
        </div>
        <div className="dw-empty">This design has no editable links.</div>
      </div>
    );
  }

  return (
    <div className="dw-panel">
      <div className="dw-panel-heading">
        <Icon name="link" size={18} />
        <h2>Links</h2>
      </div>
      <p className="dw-help-text">
        Change where the design&apos;s buttons and links point. Valid formats: https://, http://, mailto:, tel:, # or /.
      </p>

      <div className="dw-search-field">
        <Icon name="search" size={16} />
        <input
          type="search"
          className="dw-input"
          placeholder="Search links…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Search links"
        />
      </div>

      <div className="dw-link-list">
        {filteredLinks.length === 0 ? (
          <div className="dw-empty dw-empty--small">No links match your search.</div>
        ) : (
          filteredLinks.map((link) => (
            <LinkRow
              key={`${link.index}-${link.href}`}
              index={link.index}
              label={link.label}
              defaultHref={link.href}
              override={ctx.props.links?.[String(link.index)]}
              ctx={ctx}
              registerRef={registerRef}
            />
          ))
        )}
      </div>
    </div>
  );
}
